import type { TranslationKey } from '@/i18n-key';
import type { CycleEvaluationGroup } from '@/types/cycle-evaluation/CycleEvaluationGroup';

import {
    EvaluationType,
    type EvaluationTypeValue,
} from '@/constants/cycle-evaluation/CycleEvaluation';
import {
    CycleMilestoneType,
    type CycleMilestoneTypeValue,
} from '@/constants/cycle-evaluation/CycleMilestoneType';

/** The seven possible slots of the Deadlines step, in their FIXED display order. */
export const DeadlineMilestoneId = {
    PROJECT_KICK_OFF: 'projectKickOff',
    SELF_ASSESSMENT_LAUNCH: 'selfAssessmentLaunch',
    FEEDBACK_360_LAUNCH: 'feedback360Launch',
    MANAGER_ASSESSMENT_LAUNCH: 'managerAssessmentLaunch',
    MANAGER_DEADLINE: 'managerDeadline',
    DISCUSSION_EMAIL: 'discussionEmail',
    EVALUATION_CYCLE_CLOSURE: 'evaluationCycleClosure',
} as const;

export type DeadlineMilestoneIdValue =
    (typeof DeadlineMilestoneId)[keyof typeof DeadlineMilestoneId];

/** An orphan milestone dates itself only. A launch milestone also carries a due date and reminders. */
export const DeadlineMilestoneKind = {
    ORPHAN: 'orphan',
    LAUNCH: 'launch',
} as const;

export type DeadlineMilestoneKindValue =
    (typeof DeadlineMilestoneKind)[keyof typeof DeadlineMilestoneKind];

export interface OrphanMilestoneDefinition {
    id: DeadlineMilestoneIdValue;
    kind: typeof DeadlineMilestoneKind.ORPHAN;
    milestoneType: CycleMilestoneTypeValue;
    labelKey: TranslationKey;
    descriptionKey: TranslationKey;
}

export interface LaunchMilestoneDefinition {
    id: DeadlineMilestoneIdValue;
    kind: typeof DeadlineMilestoneKind.LAUNCH;
    evaluationType: EvaluationTypeValue;
    labelKey: TranslationKey;
    descriptionKey: TranslationKey;
}

export type DeadlineMilestoneDefinition = OrphanMilestoneDefinition | LaunchMilestoneDefinition;

export interface NumberedDeadlineMilestone {
    milestone: DeadlineMilestoneDefinition;
    /** Position in the VISIBLE list, 1-based — never the fixed index of `DEADLINE_MILESTONE_ORDER`. */
    number: number;
}

const isLaunchMilestone = (
    milestone: DeadlineMilestoneDefinition,
): milestone is LaunchMilestoneDefinition => milestone.kind === DeadlineMilestoneKind.LAUNCH;

const isOrphanMilestone = (
    milestone: DeadlineMilestoneDefinition,
): milestone is OrphanMilestoneDefinition => milestone.kind === DeadlineMilestoneKind.ORPHAN;

/**
 * The seven milestones in their canonical order. A launch milestone is filtered out when
 * its evaluation type is not checked at the Evaluation elements step (C4); the four orphans are
 * always present. Continuous renumbering is applied AFTER filtering — see
 * `numberVisibleDeadlineMilestones` — never derived from this fixed order's own index.
 */
const DEADLINE_MILESTONE_ORDER: readonly DeadlineMilestoneDefinition[] = [
    {
        id: DeadlineMilestoneId.PROJECT_KICK_OFF,
        kind: DeadlineMilestoneKind.ORPHAN,
        milestoneType: CycleMilestoneType.PROJECT_KICK_OFF,
        labelKey: 'cycleEvaluationDeadlines.milestones.projectKickOff.label',
        descriptionKey: 'cycleEvaluationDeadlines.milestones.projectKickOff.description',
    },
    {
        id: DeadlineMilestoneId.SELF_ASSESSMENT_LAUNCH,
        kind: DeadlineMilestoneKind.LAUNCH,
        evaluationType: EvaluationType.SELF_EVALUATION,
        labelKey: 'cycleEvaluationDeadlines.milestones.selfAssessmentLaunch.label',
        descriptionKey: 'cycleEvaluationDeadlines.milestones.selfAssessmentLaunch.description',
    },
    {
        id: DeadlineMilestoneId.FEEDBACK_360_LAUNCH,
        kind: DeadlineMilestoneKind.LAUNCH,
        evaluationType: EvaluationType.EVALUATION_360,
        labelKey: 'cycleEvaluationDeadlines.milestones.feedback360Launch.label',
        descriptionKey: 'cycleEvaluationDeadlines.milestones.feedback360Launch.description',
    },
    {
        id: DeadlineMilestoneId.MANAGER_ASSESSMENT_LAUNCH,
        kind: DeadlineMilestoneKind.LAUNCH,
        evaluationType: EvaluationType.MANAGER_EVALUATION,
        labelKey: 'cycleEvaluationDeadlines.milestones.managerAssessmentLaunch.label',
        descriptionKey: 'cycleEvaluationDeadlines.milestones.managerAssessmentLaunch.description',
    },
    {
        id: DeadlineMilestoneId.MANAGER_DEADLINE,
        kind: DeadlineMilestoneKind.ORPHAN,
        milestoneType: CycleMilestoneType.MANAGER_DEADLINE,
        labelKey: 'cycleEvaluationDeadlines.milestones.managerDeadline.label',
        descriptionKey: 'cycleEvaluationDeadlines.milestones.managerDeadline.description',
    },
    {
        id: DeadlineMilestoneId.DISCUSSION_EMAIL,
        kind: DeadlineMilestoneKind.ORPHAN,
        milestoneType: CycleMilestoneType.DISCUSSION_EMAIL,
        labelKey: 'cycleEvaluationDeadlines.milestones.discussionEmail.label',
        descriptionKey: 'cycleEvaluationDeadlines.milestones.discussionEmail.description',
    },
    {
        id: DeadlineMilestoneId.EVALUATION_CYCLE_CLOSURE,
        kind: DeadlineMilestoneKind.ORPHAN,
        milestoneType: CycleMilestoneType.EVALUATION_CYCLE_CLOSURE,
        labelKey: 'cycleEvaluationDeadlines.milestones.evaluationCycleClosure.label',
        descriptionKey: 'cycleEvaluationDeadlines.milestones.evaluationCycleClosure.description',
    },
];

/**
 * The milestones to render for the given checked evaluation types — four with none checked, up to
 * seven with all three. A launch milestone DISAPPEARS entirely when its
 * type is unchecked; it is never rendered disabled.
 */
const getVisibleDeadlineMilestones = (
    checkedEvaluationTypes: readonly EvaluationTypeValue[],
): DeadlineMilestoneDefinition[] =>
    DEADLINE_MILESTONE_ORDER.filter(
        (milestone) =>
            !isLaunchMilestone(milestone) ||
            checkedEvaluationTypes.includes(milestone.evaluationType),
    );

/**
 * Continuous numbering by visible position — the acceptance criterion, not a preference
 * ("must number milestones by visible position, continuously, with no gap"). The prototype's fixed
 * index numbering (1, 2, 5, 6, 8 when types are unchecked) is a documented bug, not to be reproduced.
 */
const numberVisibleDeadlineMilestones = (
    milestones: readonly DeadlineMilestoneDefinition[],
): NumberedDeadlineMilestone[] =>
    milestones.map((milestone, index) => ({ milestone, number: index + 1 }));

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

const startOfDay = (date: Date): Date =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());

const daysBetween = ({ start, end }: { start: Date; end: Date }): number =>
    Math.round((startOfDay(end).getTime() - startOfDay(start).getTime()) / MILLISECONDS_PER_DAY);

const addDays = ({ date, days }: { date: Date; days: number }): Date =>
    new Date(date.getTime() + days * MILLISECONDS_PER_DAY);

/**
 * The minimum number of days a launch milestone's due date must be allowed to sit after its start —
 * matches the existing `CycleSchedule.DaysAllowedToComplete` convention (`ScheduleCompletion.tsx`).
 */
const MIN_DAYS_ALLOWED_TO_COMPLETE = 1;

/**
 * The widest response window a launch milestone may open. Nothing downstream bounds it — the
 * closure milestone caps the OTHER milestones but is itself uncapped — so a launch in January with
 * a closure two years out would otherwise send a 730-day window to the server.
 */
const MAX_DAYS_ALLOWED_TO_COMPLETE = 365;

/**
 * A launch milestone's due date is not its own stored field — `CycleSchedule` only carries
 * `DaysAllowedToComplete` (days from the start), which is what "TAKE existing launch mechanics"
 * means concretely. The due-date picker shown on screen is derived FROM that count for
 * display, and any edit to it is converted BACK into a day count before saving — nothing new is
 * added to the persisted contract.
 */
const deriveDueDate = ({
    scheduledAt,
    daysAllowedToComplete,
}: {
    scheduledAt: Date;
    daysAllowedToComplete: number;
}): Date => {
    const startDate = startOfDay(scheduledAt);
    return new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate() + daysAllowedToComplete,
    );
};

const deriveDaysAllowedToComplete = ({
    scheduledAt,
    dueAt,
}: {
    scheduledAt: Date;
    dueAt: Date;
}): number =>
    Math.max(MIN_DAYS_ALLOWED_TO_COMPLETE, daysBetween({ start: scheduledAt, end: dueAt }));

const ORPHAN_MILESTONE_DEFINITIONS: readonly OrphanMilestoneDefinition[] =
    DEADLINE_MILESTONE_ORDER.filter(isOrphanMilestone);

const LAUNCH_MILESTONE_BY_TYPE: Readonly<Record<EvaluationTypeValue, LaunchMilestoneDefinition>> =
    Object.fromEntries(
        DEADLINE_MILESTONE_ORDER.filter(isLaunchMilestone).map((milestone) => [
            milestone.evaluationType,
            milestone,
        ]),
    ) as Record<EvaluationTypeValue, LaunchMilestoneDefinition>;

/**
 * The evaluation types actually grouped under the cycle, read from `cycleEvaluations` — the exact
 * same source `groupIncludesFeedback360` reads from one step earlier (`cycleEvaluationGroupSteps.ts`),
 * generalized from "is 360 among them" to "which ones are". An unloaded or childless group has none.
 */
const getCheckedEvaluationTypes = (
    group?: Pick<CycleEvaluationGroup, 'cycleEvaluations'>,
): EvaluationTypeValue[] =>
    (group?.cycleEvaluations ?? []).map((evaluation) => evaluation.reviewType);

export {
    isLaunchMilestone,
    getVisibleDeadlineMilestones,
    numberVisibleDeadlineMilestones,
    daysBetween,
    addDays,
    MIN_DAYS_ALLOWED_TO_COMPLETE,
    MAX_DAYS_ALLOWED_TO_COMPLETE,
    deriveDueDate,
    deriveDaysAllowedToComplete,
    ORPHAN_MILESTONE_DEFINITIONS,
    LAUNCH_MILESTONE_BY_TYPE,
    getCheckedEvaluationTypes,
};
